import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Check, Building2, Users, Zap, Crown, ChevronDown } from 'lucide-react';
import { Logo } from '@/components/logo';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const EASE_CURVE = [0.25, 0.46, 0.45, 0.94];

const TIERS = [
  {
    name: 'BOHSA Clients',
    icon: Users,
    price: 0,
    accent: 'emerald',
    includedTag: 'Free for BOHSA clients',
    description: 'Full platform access included free for BOHSA clients.',
    maxUsers: 'Unlimited users',
    spaces: 'Unlimited Spaces',
    features: [
      { text: 'All platform ', link: { label: 'tools', href: '/tools' } },
      'Team management',
      'Compliance tracking',
      'Email support',
      '100 SMS credits/month',
      '50 AI credits/month',
    ],
  },
  {
    name: 'Professional',
    icon: Zap,
    price: 6,
    accent: 'violet',
    popular: true,
    description: 'For growing teams that need collaboration and scheduling tools.',
    maxUsers: 'Up to 50 users',
    spaces: 'Unlimited Spaces',
    includesFrom: 'Core features',
    features: [
      'Bookings & scheduling',
      'Document management',
      'Message board & posts',
      'Checklists & tasks',
      'Priority support',
    ],
  },
  {
    name: 'Business',
    icon: Building2,
    price: 9,
    accent: 'amber',
    description: 'For organisations needing automation and advanced tools.',
    maxUsers: 'Unlimited users',
    spaces: 'Unlimited Spaces',
    includesFrom: 'Professional',
    features: [
      'Attendance & leave management',
      'Workflow automation',
      'Site check-ins & QR codes',
      'Advanced reporting',
      'Custom integrations',
      'Dedicated account manager',
    ],
  },
  {
    name: 'Enterprise',
    icon: Crown,
    price: null,
    accent: 'sky',
    description: 'For large organisations with bespoke security and support.',
    maxUsers: 'Unlimited users',
    spaces: 'Unlimited Spaces',
    includesFrom: 'Business',
    features: [
      'SSO & advanced security',
      'Custom onboarding',
      'SLA guarantee',
      'API access',
      'White-label options',
    ],
  },
];

const TEAM_SIZES = [
  { value: '5', label: '5 seats' },
  { value: '10', label: '10 seats' },
  { value: '25', label: '25 seats' },
  { value: '50', label: '50 seats' },
  { value: '100', label: '100 seats' },
];

const ACCENT_CLASSES = {
  emerald: {
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    icon: 'bg-emerald-50 text-emerald-600',
    button: 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-600/20',
    check: 'text-emerald-500',
    ring: '',
    gradient: 'from-emerald-500/5 to-transparent',
  },
  violet: {
    badge: 'bg-violet-50 text-violet-700 border-violet-200',
    icon: 'bg-violet-50 text-violet-600',
    button: 'bg-violet-600 hover:bg-violet-700 text-white shadow-lg shadow-violet-600/20',
    check: 'text-violet-500',
    ring: 'ring-2 ring-violet-500/20 border-violet-300',
    gradient: 'from-violet-500/5 to-transparent',
  },
  amber: {
    badge: 'bg-amber-50 text-amber-700 border-amber-200',
    icon: 'bg-amber-50 text-amber-600',
    button: 'bg-amber-600 hover:bg-amber-700 text-white shadow-lg shadow-amber-600/20',
    check: 'text-amber-500',
    ring: '',
    gradient: 'from-amber-500/5 to-transparent',
  },
  sky: {
    badge: 'bg-sky-50 text-sky-700 border-sky-200',
    icon: 'bg-sky-50 text-sky-600',
    button: 'bg-sky-600 hover:bg-sky-700 text-white shadow-lg shadow-sky-600/20',
    check: 'text-sky-500',
    ring: '',
    gradient: 'from-sky-500/5 to-transparent',
  },
};

const FAQ_ITEMS = [
  {
    q: 'Can I switch plans at any time?',
    a: 'Yes. You can upgrade or downgrade your plan at any time. Changes take effect at the start of your next billing cycle, and any difference is prorated.',
  },
  {
    q: 'What counts as a seat?',
    a: 'A seat is any active user in your organisation. Deactivated users do not count towards your seat total.',
  },
  {
    q: 'Is there a free trial?',
    a: 'We offer a 14-day free trial on all plans with no credit card required. You can explore every feature before committing.',
  },
  {
    q: 'What payment methods do you accept?',
    a: 'We accept all major credit and debit cards. Enterprise customers can also pay by invoice with NET 30 terms.',
  },
  {
    q: 'Can I cancel at any time?',
    a: 'Absolutely. There are no long-term contracts. You can cancel your subscription at any time and retain access until the end of your billing period.',
  },
];

// -------------------------------------------------------------------
// Sub-components
// -------------------------------------------------------------------

const BillingToggle = ({ isYearly, onToggle }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: 0.2, ease: EASE_CURVE }}
    className="flex items-center justify-center gap-3"
  >
    <button
      onClick={() => onToggle(false)}
      className={`px-5 py-2.5 rounded-full font-medium text-sm transition-all duration-300 ${
        !isYearly
          ? 'bg-slate-900 text-white shadow-lg shadow-slate-900/20'
          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
      }`}
    >
      Monthly
    </button>
    <button
      onClick={() => onToggle(true)}
      className={`px-5 py-2.5 rounded-full font-medium text-sm transition-all duration-300 flex items-center gap-2 ${
        isYearly
          ? 'bg-slate-900 text-white shadow-lg shadow-slate-900/20'
          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
      }`}
    >
      Yearly
      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
        Save 20%
      </span>
    </button>
  </motion.div>
);

const TeamSizeSelector = ({ teamSize, onTeamSizeChange }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: 0.3, ease: EASE_CURVE }}
    className="flex items-center justify-center gap-3"
  >
    <span className="text-sm font-medium text-slate-500">Team size:</span>
    <Select value={teamSize} onValueChange={onTeamSizeChange}>
      <SelectTrigger className="w-[140px] h-9 rounded-full border-slate-200 bg-white text-sm font-medium">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {TEAM_SIZES.map((size) => (
          <SelectItem key={size.value} value={size.value}>
            {size.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </motion.div>
);

const PricingCard = ({ tier, isYearly, teamSize, index }) => {
  const navigate = useNavigate();
  const colors = ACCENT_CLASSES[tier.accent];
  const Icon = tier.icon;

  const isFree = tier.price === 0;
  const isCustom = tier.price === null;
  const monthlyPrice = !isFree && !isCustom
    ? isYearly
      ? (tier.price * 0.8).toFixed(2)
      : tier.price.toFixed(2)
    : null;

  const totalMonthly = monthlyPrice
    ? (parseFloat(monthlyPrice) * parseInt(teamSize)).toFixed(2)
    : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.3 + index * 0.1, ease: EASE_CURVE }}
      className={`relative flex flex-col rounded-2xl bg-white border transition-all duration-300 hover:shadow-[0_8px_30px_-12px_rgba(0,0,0,0.12)] hover:scale-[1.02] ${
        tier.popular
          ? `${colors.ring} border-violet-300`
          : 'border-slate-200 hover:border-slate-300'
      }`}
    >
      {/* Popular badge */}
      {tier.popular && (
        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 z-10">
          <span className="text-xs font-semibold px-4 py-1.5 rounded-full bg-violet-600 text-white shadow-lg shadow-violet-600/30 whitespace-nowrap">
            Most Popular
          </span>
        </div>
      )}

      {/* Gradient top accent */}
      <div className={`h-1 rounded-t-2xl bg-gradient-to-r ${colors.gradient}`} />

      <div className="flex flex-col flex-1 p-6 pt-5">
        {/* Header */}
        <div className="flex items-center gap-3 mb-3">
          <div className={`p-2 rounded-xl ${colors.icon}`}>
            <Icon className="h-5 w-5" />
          </div>
          <h3 className="text-lg font-bold text-slate-800">{tier.name}</h3>
        </div>

        <p className="text-sm text-slate-500 mb-5 leading-relaxed min-h-[40px]">{tier.description}</p>

        {/* Price - fixed height so buttons align across all cards */}
        <div className="mb-5 min-h-[120px]">
          {isFree ? (
            <>
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-bold text-slate-800 tracking-tight">Free</span>
              </div>
              {tier.includedTag && (
                <span className="inline-block mt-2 text-xs font-medium px-3 py-1.5 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200">
                  {tier.includedTag}
                </span>
              )}
            </>
          ) : isCustom ? (
            <>
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-bold text-slate-800 tracking-tight">Custom</span>
              </div>
              <p className="text-sm text-slate-500 mt-1">
                Tailored to your organisation
              </p>
            </>
          ) : (
            <>
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-bold text-slate-800 tracking-tight">
                  {'\u00A3'}{monthlyPrice}
                </span>
                <span className="text-sm text-slate-500 font-medium">/seat/month</span>
              </div>
              {isYearly && (
                <p className="text-xs text-slate-400 mt-1">
                  Billed annually at {'\u00A3'}{(parseFloat(monthlyPrice) * 12).toFixed(2)}/seat/year
                </p>
              )}
              <div className="mt-3 px-3 py-2 rounded-lg bg-slate-50 border border-slate-100">
                <p className="text-sm font-semibold text-slate-700">
                  Total: {'\u00A3'}{totalMonthly}
                  <span className="text-slate-400 font-normal">/month</span>
                </p>
                <p className="text-xs text-slate-400 mt-0.5">
                  for {teamSize} seats
                </p>
              </div>
            </>
          )}
        </div>

        {/* CTA Button */}
        <Button
          onClick={() => navigate('/signup')}
          className={`w-full rounded-xl h-11 font-semibold text-sm mb-6 ${colors.button}`}
        >
          {isCustom ? 'Contact Us' : isFree ? 'Get Started Free' : 'Get Started'}
        </Button>

        {/* Tier limits */}
        <div className="flex flex-col gap-1.5 mb-4 pb-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${colors.badge}`}>
              {tier.maxUsers}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${colors.badge}`}>
              {tier.spaces}
            </span>
          </div>
        </div>

        {/* Includes from */}
        {tier.includesFrom && (
          <p className="text-xs font-medium text-slate-400 mb-3">
            Everything in {tier.includesFrom}, plus:
          </p>
        )}

        {/* Features */}
        <ul className="space-y-2.5 flex-1">
          {tier.features.map((feature, i) => (
            <li key={typeof feature === 'string' ? feature : `feature-${i}`} className="flex items-start gap-2.5">
              <Check className={`h-4 w-4 mt-0.5 shrink-0 ${colors.check}`} />
              <span className="text-sm text-slate-600 leading-snug">
                {typeof feature === 'string' ? feature : (
                  <>
                    {feature.text}
                    <a href={feature.link.href} className="text-violet-600 hover:text-violet-700 underline underline-offset-2 font-medium">
                      {feature.link.label}
                    </a>
                  </>
                )}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </motion.div>
  );
};

const FAQItem = ({ item, index }) => {
  const [open, setOpen] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.1 + index * 0.06, ease: EASE_CURVE }}
      className="rounded-xl bg-white border border-slate-200 overflow-hidden transition-all duration-300 hover:border-slate-300"
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 text-left"
      >
        <span className="font-semibold text-slate-800 text-sm pr-4">{item.q}</span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-slate-400 transition-transform duration-300 ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>
      <motion.div
        initial={false}
        animate={{ height: open ? 'auto' : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.3, ease: EASE_CURVE }}
        className="overflow-hidden"
      >
        <p className="px-5 pb-4 text-sm text-slate-500 leading-relaxed">{item.a}</p>
      </motion.div>
    </motion.div>
  );
};

// -------------------------------------------------------------------
// Main Page Component
// -------------------------------------------------------------------

export default function PricingPage() {
  const navigate = useNavigate();
  const [isYearly, setIsYearly] = useState(false);
  const [teamSize, setTeamSize] = useState('10');

  useEffect(() => {
    document.title = 'Pricing';
    return () => { document.title = 'Training Matrix'; };
  }, []);

  return (
    <div className="min-h-screen bg-[#fcf8f3]">
      {/* Simple header / nav */}
      <header className="sticky top-0 z-50 bg-[#fcf8f3]/80 backdrop-blur-md border-b border-slate-200/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2.5 group"
          >
            <Logo height={32} iconOnly className="group-hover:opacity-80 transition-opacity" />
          </button>
          <Button
            variant="outline"
            onClick={() => navigate('/login')}
            className="rounded-full px-5 text-sm font-medium border-slate-200 hover:bg-white"
          >
            Login
          </Button>
        </div>
      </header>

      {/* Hero section */}
      <section className="pt-16 pb-10 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: EASE_CURVE }}
        >
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-800 tracking-tight leading-tight">
            Simple, transparent pricing
          </h1>
          <p className="mt-4 text-lg text-slate-500 max-w-2xl mx-auto leading-relaxed">
            Choose the plan that fits your team. Start with a 14-day free trial,
            no credit card required. Scale as you grow.
          </p>
        </motion.div>

        {/* Controls row */}
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8">
          <BillingToggle isYearly={isYearly} onToggle={setIsYearly} />
          <div className="hidden sm:block w-px h-8 bg-slate-200" />
          <TeamSizeSelector teamSize={teamSize} onTeamSizeChange={setTeamSize} />
        </div>
      </section>

      {/* Pricing grid */}
      <section className="pb-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 pt-6">
          {TIERS.map((tier, index) => (
            <PricingCard
              key={tier.name}
              tier={tier}
              isYearly={isYearly}
              teamSize={teamSize}
              index={index}
            />
          ))}
        </div>
      </section>

      {/* Comparison note */}
      <section className="pb-16 px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.8, ease: EASE_CURVE }}
          className="rounded-2xl bg-white border border-slate-200 p-8 text-center"
        >
          <h2 className="text-xl font-bold text-slate-800 mb-2">
            Not sure which plan is right for you?
          </h2>
          <p className="text-sm text-slate-500 mb-6 max-w-lg mx-auto leading-relaxed">
            Start with a free 14-day trial of the Professional plan. No credit card
            needed. You can switch plans or cancel at any time.
          </p>
          <div className="flex items-center justify-center gap-3">
            <Button
              onClick={() => navigate('/signup')}
              className="rounded-xl h-11 px-8 font-semibold text-sm bg-slate-900 hover:bg-slate-800 text-white shadow-lg shadow-slate-900/20"
            >
              Start Free Trial
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/signup')}
              className="rounded-xl h-11 px-8 font-semibold text-sm border-slate-200 hover:bg-slate-50"
            >
              Contact Sales
            </Button>
          </div>
        </motion.div>
      </section>

      {/* FAQ section */}
      <section className="pb-20 px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4, ease: EASE_CURVE }}
          className="text-center mb-10"
        >
          <h2 className="text-2xl font-bold text-slate-800 tracking-tight">
            Frequently asked questions
          </h2>
          <p className="mt-2 text-sm text-slate-500">
            Everything you need to know about our pricing.
          </p>
        </motion.div>
        <div className="space-y-3">
          {FAQ_ITEMS.map((item, index) => (
            <FAQItem key={item.q} item={item} index={index} />
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200/60 bg-white/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <Logo height={24} iconOnly className="opacity-60" />
            <span className="text-sm text-slate-500">
              Training Matrix {'\u00A9'} {new Date().getFullYear()}
            </span>
          </div>
          <div className="flex items-center gap-6 text-sm text-slate-500">
            <button
              onClick={() => navigate('/login')}
              className="hover:text-slate-800 transition-colors"
            >
              Login
            </button>
            <button
              onClick={() => navigate('/signup')}
              className="hover:text-slate-800 transition-colors"
            >
              Sign Up
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}
